'use client';

import type { LinkProps } from 'next/link';
import type { AnchorHTMLAttributes, CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import type { Spacing, SpacingType} from '@zoomit/primitives';
import { Link, spacingHelper } from '@zoomit/primitives';

import type { ButtonSize} from '../../css/link-button';
import { linkButton } from '../../css/link-button';
import type { ColorScheme } from '../../theme/button';
import type { ButtonVariant } from '../button';
import { TextIcon } from '../text-icon';

type AnchorBaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  colorScheme?: ColorScheme;
  gap?: keyof typeof Spacing;
  disabled?: boolean;
  spacing?: SpacingType;
  fontWeight?: CSSProperties['fontWeight'];
};

const AnchorBase = styled(Link)<{
  variant: ButtonVariant;
  $colorScheme: ColorScheme;
  size: ButtonSize;
  disabled?: boolean;
  spacing?: SpacingType;
  fontWeight?: CSSProperties['fontWeight'];
}>`
  ${({ size, $colorScheme, variant, disabled, fontWeight }) =>
    linkButton({ size, variant, colorScheme: $colorScheme, disabled, fontWeight })};
  text-decoration: none;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ spacing }) => spacing && spacingHelper(spacing)};
`;

type AnchorProps = {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  href: string;
} & AnchorBaseProps;

export type Props = AnchorHTMLAttributes<HTMLAnchorElement> & Omit<LinkProps, 'as'> & AnchorProps;

export const Anchor = (props: Props) => {
  const {
    size = 'medium',
    variant = 'outline',
    colorScheme = 'default',
    gap = size === 'small' ? 'x' : 'x2',
    leftIcon,
    children,
    rightIcon,
    ...rest
  } = props;

  // values comes from props should be in priority to customizing be possible

  return (
    <AnchorBase variant={variant} $colorScheme={colorScheme} size={size} {...rest}>
      <TextIcon gap={gap} leftIcon={leftIcon} rightIcon={rightIcon}>
        {children}
      </TextIcon>
    </AnchorBase>
  );
};
